import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Share2, Trash2, Link2, MessageCircle, Mail, CheckCircle2, Clock, XCircle, QrCode } from "lucide-react";
import { toast } from "sonner";
import SupporterQRModal from "@/components/SupporterQRModal";

/**
 * Componente Area Riservata: gestione richieste supporter.
 * - Genera un link condivisibile con un click.
 * - Mostra le richieste pending/accepted/declined con azioni (copia, WhatsApp, email, revoca).
 * - Pulsante QR che apre un modal con QR-code + download PNG/PDF impaginato.
 */
export default function SupporterInviteBox({ teamName, coachName }) {
  const [requests, setRequests] = useState({ items: [], total: 0 });
  const [creating, setCreating] = useState(false);
  const [supporters, setSupporters] = useState({ supported_by: [] });
  const [qrState, setQrState] = useState(null); // { url, teamName, coachName } | null

  const load = async () => {
    try {
      const [reqs, sups] = await Promise.all([
        api.get("/me/supporter/requests"),
        api.get("/me/supporters"),
      ]);
      setRequests(reqs.data);
      setSupporters(sups.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async ({ nativeShare = false } = {}) => {
    setCreating(true);
    try {
      const { data } = await api.post("/me/supporter/request");
      const shareUrl = `${window.location.origin}${data.share_path}`;
      const teamName = data.target_team || "la mia squadra";
      const shareText = `Ehi! Diventa supporter della mia squadra "${teamName}" nel Fantacampionato FIFE 🦉⚽`;
      // 1) Preferisci il native share dell'OS (WhatsApp/Telegram/Mail/AirDrop/…)
      if (nativeShare && typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({
            title: "Diventa mio supporter · FIFE",
            text: shareText,
            url: shareUrl,
          });
          toast.success("Link condiviso");
          load();
          return;
        } catch (e) {
          // Utente ha annullato lo share sheet → fallback silenzioso al copy
          if (e?.name === "AbortError") { load(); return; }
        }
      }
      // 2) Fallback: copia negli appunti
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link generato e copiato negli appunti");
      } catch {
        toast.success("Link generato");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione link");
    } finally { setCreating(false); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revocare questa richiesta pendente?")) return;
    try {
      await api.delete(`/me/supporter/request/${token}`);
      toast.success("Richiesta revocata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore revoca");
    }
  };

  const openQR = async () => {
    // Genera il link supporter (o riusa uno pending) e apre il modal QR
    setCreating(true);
    try {
      const { data } = await api.post("/me/supporter/request");
      const shareUrl = `${window.location.origin}${data.share_path}`;
      setQrState({
        url: shareUrl,
        teamName: teamName || data.target_team,
        coachName: coachName,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione QR");
    } finally { setCreating(false); }
  };

  const url = (token) => `${window.location.origin}/supporter/join/${token}`;
  const shareWhatsApp = (token, team) => {
    const text = encodeURIComponent(`Ehi! Diventa supporter della mia squadra "${team}" nel Fantacalcio FIFE: ${url(token)}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareEmail = (token, team) => {
    const subject = encodeURIComponent(`Diventa supporter di ${team} nel Fantacalcio FIFE`);
    const body = encodeURIComponent(`Ciao!\n\nHo appena partecipato al Fantacalcio FIFE 2026/27. Mi daresti una mano diventando il mio supporter?\n\nBasta cliccare qui e confermare: ${url(token)}\n\nGrazie!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const copy = async (token) => {
    try { await navigator.clipboard.writeText(url(token)); toast.success("Link copiato"); }
    catch { toast.error("Impossibile copiare"); }
  };

  const pending = requests.items.filter((r) => r.status === "pending");
  const accepted = requests.items.filter((r) => r.status === "accepted");
  const declined = requests.items.filter((r) => r.status === "declined" || r.status === "revoked");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="supporter-invite-box">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500">Cerca supporter</div>
          <div className="display font-black text-xl">Invita chi vuoi con un link</div>
          <div className="text-xs text-slate-500 mt-1">
            Con <b>Condividi</b> si apre lo share nativo (WhatsApp, Telegram, Email…). Chi riceve il link inserisce nome, cognome ed email e diventa parte della tua fanbase.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Button onClick={() => generate({ nativeShare: true })} disabled={creating}
                  className="btn-brand rounded-full h-11 px-5 shadow-md hover:shadow-lg transition-shadow"
                  data-testid="supporter-share-btn">
            <Share2 className="w-4 h-4 mr-2" /> {creating ? "Attendi…" : "Condividi"}
          </Button>
          <Button onClick={openQR} disabled={creating}
                  variant="outline" className="rounded-full h-11 px-4 border-slate-300"
                  data-testid="supporter-qr-btn" title="Mostra QR code stampabile">
            <QrCode className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">QR</span>
          </Button>
          <Button onClick={() => generate({ nativeShare: false })} disabled={creating}
                  variant="outline" className="rounded-full h-11 px-4 border-slate-300"
                  data-testid="supporter-generate-link" title="Genera un link e copialo negli appunti">
            <Link2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Solo link</span>
          </Button>
        </div>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-3 gap-2">
        <StatChip label="Supporter attivi" value={supporters.count_supported_by ?? (supporters.supported_by || []).length} color="#22c55e" testid="supporter-count-active" />
        <StatChip label="In attesa" value={pending.length} color="#f59e0b" testid="supporter-count-pending" />
        <StatChip label="Rifiutati" value={declined.length} color="#94a3b8" testid="supporter-count-declined" />
      </div>

      {/* Richieste pending */}
      {pending.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Link attivi in attesa di risposta
          </div>
          <ul className="space-y-2">
            {pending.map((r) => (
              <li key={r.token} className="rounded-xl border border-slate-200 p-3" data-testid={`pending-req-${r.token}`}>
                <div className="text-xs font-mono truncate text-slate-500 mb-2">{url(r.token)}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => copy(r.token)} className="rounded-full h-8" data-testid={`copy-${r.token}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copia
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareWhatsApp(r.token, r.target_team)}
                          className="rounded-full h-8" style={{ borderColor: "#25d366", color: "#128c7e" }} data-testid={`wa-${r.token}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareEmail(r.token, r.target_team)}
                          className="rounded-full h-8" data-testid={`mail-${r.token}`}>
                    <Mail className="w-3.5 h-3.5 mr-1" /> Email
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => revoke(r.token)}
                          className="rounded-full h-8 border-red-200 text-red-600 hover:bg-red-50" data-testid={`revoke-${r.token}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoca
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Storico accepted / declined compatto */}
      {accepted.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Diventati supporter tramite link
          </div>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {accepted.map((r) => (
              <li key={r.token} className="text-sm text-slate-700 flex items-center gap-2" data-testid={`accepted-${r.token}`}>
                <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#22c55e" }} />
                <span className="font-semibold">{r.external_full_name}</span>
                <span className="text-slate-400 text-xs">· {r.external_email}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {declined.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1">
            <XCircle className="w-3 h-3" /> Non hanno accettato / revocati
          </div>
          <div className="text-xs text-slate-400">
            {declined.length} link {declined.length === 1 ? "chiuso" : "chiusi"}.
          </div>
        </div>
      )}
      {qrState && (
        <SupporterQRModal
          shareUrl={qrState.url}
          teamName={qrState.teamName}
          coachName={qrState.coachName}
          onClose={() => setQrState(null)}
        />
      )}
    </div>
  );
}

function StatChip({ label, value, color, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 px-3 py-2 bg-slate-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="display font-black text-2xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
